import * as React from "react";

/**
 * Shim local para @radix-ui/react-compose-refs.
 *
 * Evita loop infinito quando callback ref interno dispara setState.
 * Estratégia: callback estável + short-circuit para nó repetido
 * + memoização de composeRefs para manter identidade entre renders.
 */

type RefPossivel<T> = React.Ref<T> | undefined;

type EntradaCache<T> = {
  refs: RefPossivel<T>[];
  callback: (node: T | null) => void;
};

const LIMITE_CACHE = 200;
const cacheComposeRefs: EntradaCache<unknown>[] = [];

function setRef<T>(ref: RefPossivel<T>, valor: T | null) {
  if (typeof ref === "function") {
    ref(valor);
    return;
  }

  if (ref && "current" in ref && !Object.is(ref.current, valor)) {
    (ref as React.MutableRefObject<T | null>).current = valor;
  }
}

function refsSaoIguais<T>(a: RefPossivel<T>[], b: RefPossivel<T>[]) {
  return a.length === b.length && a.every((ref, i) => Object.is(ref, b[i]));
}

function composeRefs<T>(...refs: RefPossivel<T>[]) {
  const entradaExistente = cacheComposeRefs.find((entrada) => refsSaoIguais(entrada.refs as RefPossivel<T>[], refs));
  if (entradaExistente) {
    return entradaExistente.callback as (node: T | null) => void;
  }

  let ultimoNo: T | null = null;
  const callback = (node: T | null) => {
    if (Object.is(ultimoNo, node)) return;
    ultimoNo = node;
    refs.forEach((ref) => setRef(ref, node));
  };

  cacheComposeRefs.push({ refs: [...refs], callback: callback as (node: unknown | null) => void });
  if (cacheComposeRefs.length > LIMITE_CACHE) cacheComposeRefs.shift();

  return callback;
}

function useComposedRefs<T>(...refs: RefPossivel<T>[]) {
  const refsAtuais = React.useRef(refs);
  const ultimoNo = React.useRef<T | null>(null);

  refsAtuais.current = refs;

  return React.useCallback((node: T | null) => {
    if (Object.is(ultimoNo.current, node)) return;
    ultimoNo.current = node;
    refsAtuais.current.forEach((ref) => setRef(ref, node));
  }, []);
}

export { composeRefs, useComposedRefs };
